<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import '@/assets/styles/map/styles.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// Fix for default marker icon in production
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
})

import { useDataStore } from '@/modules/data/stores/data.store'
import { usePrimariesMapStore } from '@/modules/primaries/stores/primaries-map.store'

import PrimariesElectionSummary from '@/modules/primaries/components/PrimariesElectionSummary.vue'
import MapHeader from '@/modules/map/components/MapHeader.vue'
import Legend from '@/modules/map/components/Legend.vue'
import MapSearch from '@/modules/map/components/MapSearch.vue'
import { defaultRegionLayerStyle, selectedLayerStyle } from '@/modules/map/utils/map-layer-styles'
import { COLORS } from '../../utils/colors'
import { CONSTANTS } from '../../utils/constants'
import { STRINGS } from '../../utils/strings'

const mapContainer = ref<HTMLElement | null>(null)
const dataStore = useDataStore()
const primariesStore = usePrimariesMapStore()
const regionsLayer = ref<L.GeoJSON | null>(null)

const isLegendVisible = ref(true)

onMounted(async () => {
    await dataStore.getShapeFiles()
    primariesStore.loadData()

    if (mapContainer.value) {
        primariesStore.initializeMap(mapContainer.value)
        addRegionsLayer()
        addHeadquartersMarker()
    }
})



function addRegionsLayer() {
    if (!primariesStore.map || !dataStore.sixteenRegionsGeoJson) return

    regionsLayer.value = L.geoJSON(dataStore.sixteenRegionsGeoJson, {
        style: (feature: any) => {
            const regionName = feature?.properties?.region?.trim()
            const fillColor = primariesStore.getRegionFillColor(regionName)
            return {
                ...defaultRegionLayerStyle,
                fillColor: fillColor,
                fillOpacity: 0.8 // Increase opacity to make shades visible
            }
        },
        onEachFeature: (feature, layer) => {
             const rawRegionName = feature.properties?.region.trim()
             let regionName = rawRegionName
             
             // Normalize Savanna to match data source "SAVANNA"
             if (regionName.toUpperCase().includes('SAVANNA')) {
                 regionName = 'SAVANNA'
             }

             // Find winner color or simplified color for now
             const regionData = primariesStore.regions.find(r => r.region === regionName)
             
             // Always bind click if it's a valid region, or at least attempt it
             if (regionData || regionName) { 
                 layer.on('click', (e) => {
                     primariesStore.selectRegion(regionName)
                     primariesStore.zoomToFeature(e.target.getBounds())
                 })
             }
        }
    }).addTo(primariesStore.map)
}

function addHeadquartersMarker() {
     if (!primariesStore.map) return

    const hqData = primariesStore.regions.find(r => r.region === CONSTANTS.REGIONS.HEADQUARTERS)
    if (hqData) {
        // Approximate location for NPP Headquarters in Accra
        const hqLocation: [number, number] = [5.5600, -0.2057] 
        
        const marker = L.marker(hqLocation).addTo(primariesStore.map)
        
        marker.on('click', () => {
            primariesStore.selectRegion(CONSTANTS.REGIONS.HEADQUARTERS)
        })
    }
}

const mapHeader = computed(() => {
  return STRINGS.HEADERS.PRIMARIES_TITLE
})

const handleSearch = (query: string) => {
  primariesStore.searchFeatures(query)
}

const handleSearchSelect = (result: any) => {
    if (primariesStore.map) {
         // Find layer and zoom/highlight
         primariesStore.selectRegion(result.name)
    }
}

const handleSearchHighlight = (result: any) => {
    // Implement highlight if needed
}

const handleSearchClear = () => {
    primariesStore.searchFeatures('')
}

const toggleLegend = () => {
    isLegendVisible.value = !isLegendVisible.value
}

// Dropdown Logic
const selectedDropdownRegion = ref<any>(null)

const regionOptions = computed(() => {
    // Map regions to Dropdown format
    // Ensure we have unique names and handle Savanna normalization if needed for display
    return primariesStore.regions.map(r => ({ name: r.region })).sort((a, b) => a.name.localeCompare(b.name))
})

const handleDropdownChange = (event: any) => {
    const selected = event.value
    if (selected && selected.name) {
        // trigger selection and zoom
        const regionName = selected.name
        primariesStore.selectRegion(regionName)
        
        // Find layer to zoom
        if (regionsLayer.value) {
            regionsLayer.value.eachLayer((layer: any) => {
                const featureName = layer.feature.properties?.region.trim()
                let normalized = featureName
                if (featureName.toUpperCase().includes('SAVANNA')) normalized = 'SAVANNA'
                
                if (normalized === regionName) {
                    primariesStore.zoomToFeature(layer.getBounds())
                }
            })
        }
    } 
    // Dropdown change logic typically adds. If we want "select only this", we'd need to clear first. 
    // But user asked for additive. We'll stick to store's toggle/add behavior.
    // However, Dropdown UI is single select, so it "selects" a value.
}

// Sync Dropdown with Store Selection
watch(() => primariesStore.selectedRegionNames, (newNames) => {
    if (newNames.length > 0) {
        // Sync to the last selected Item for display, or clears if empty
        const lastName = newNames[newNames.length - 1]
        const option = regionOptions.value.find(o => o.name === lastName)
        selectedDropdownRegion.value = option || null
    } else {
        selectedDropdownRegion.value = null
    }
})

watch(() => [primariesStore.selectedRegionNames, primariesStore.electionSummary], () => {
    if (!regionsLayer.value) return
    const selectedNames = primariesStore.selectedRegionNames
    const hasSelection = selectedNames.length > 0

    regionsLayer.value.eachLayer((layer: any) => {
        const featureName = layer.feature.properties?.region.trim()
        
        let normalizedFeatureName = featureName
        if (featureName.toUpperCase().includes('SAVANNA')) {
            normalizedFeatureName = 'SAVANNA'
        }

        const dynamicFill = primariesStore.getRegionFillColor(featureName)
        const isSelected = selectedNames.includes(normalizedFeatureName)

        if (isSelected) {
            // Selected: Keep shade, add highlighting border
            layer.setStyle({
                weight: 4,
                color: '#333',
                fillOpacity: 0.9,
                fillColor: dynamicFill
            })
            if (layer.bringToFront) layer.bringToFront()
        } else if (hasSelection) {
            // Unselected but Selection Exists: Fade out (Grey/dim)
            layer.setStyle({
                weight: 1,
                color: '#ccc',
                fillOpacity: 0.3,
                fillColor: '#eee' // Neutral grey to de-emphasize
            })
        } else {
            // No Selection: Default dynamic shading for all
            layer.setStyle({
                ...defaultRegionLayerStyle,
                fillColor: dynamicFill,
                fillOpacity: 0.8
            })
        }
    })
})
</script>

<template>
  <div class="parent">
    <div class="presidential-container">
      <MapHeader :title="mapHeader" />

      <div class="presidential">
        <div class="map-statistics">
          <div class="map-statistics-container">
            <div class="map-statistics-content">
              <PrimariesElectionSummary year="2023"
                :summary="primariesStore.electionSummary" />
            </div>
            <div class="map-statistics-legend">
              <Button class="control-button" @click="toggleLegend" text>
                {{ isLegendVisible ? STRINGS.BUTTONS.HIDE_LEGEND : STRINGS.BUTTONS.SHOW_LEGEND }}
              </Button>
              <Legend v-if="isLegendVisible" title="" :items="primariesStore.legendItems"
                :horizontal="true" />
            </div>
          </div>
        </div>

        <div class="map-content">
          <div class="map-controls">
            <div class="map-controls-left">
               <Button class="control-button" @click="primariesStore.resetStore" text>{{ STRINGS.BUTTONS.RESET }}</Button>
               
               <span v-if="primariesStore.selectedRegionNames.length" class="selected-region-label">
                   {{ primariesStore.selectedRegionNames.join(', ') }}
               </span>
            </div>
            <div class="map-controls-right">
               <Dropdown 
                   v-model="selectedDropdownRegion" 
                   :options="regionOptions" 
                   optionLabel="name" 
                   placeholder="Select a Region" 
                   filter 
                   showClear
                   class="w-full md:w-14rem"
                   @change="handleDropdownChange"
               />
            </div>
          </div>

          <div ref="mapContainer" class="map-container"></div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.map-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.map-controls-left {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.selected-region-label {
    font-weight: bold;
    font-size: 1.1rem;
    color: #333;
}

.map-controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
